import {get, map} from 'lodash'
import React, {useEffect, useState, useContext} from "react";
import {DataGridWrapper} from "../../../../Utils/DataGridWrapper/DataGridWrapper";
import {getCompanyAutomations} from "../../../../Services/api";
import {useUserAdminOrgs} from "../../../Hooks/UseUserAdminOrgs";
import LoadingSpinner from "../../../LoadingSpinner/LoadingSpinner";
import {DeleteAutomationCell} from "../DeleteAutomationCell/DeleteAutomationCell";
import {EditAutomationCell} from "../EditAutomationCell/EditAutomationCell";
import {
    CreateAutomationModal
} from "../../../Notifications/NotificationAutomationButton/CreateAutomationModal/CreateAutomationModal";
import {AutomationDetailsCell} from "../AutomationDetailsCell/AutomationDetailsCell";
import ScheduleIcon from '@mui/icons-material/Schedule';
import SendIcon from '@mui/icons-material/Send';
import PauseIcon from '@mui/icons-material/Pause';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import './AutomationsTable.scss'
import {SchedulingModes} from "../../../Notifications/NotificationsLib";
import {ScrollableListCell} from "../../../../Utils/ScrollableListCell/ScrollableListCell";
import {AutomationStatus} from "../RemindersLib";
import {I18nReactContext} from "../../../../contexts/I18nContext";
import {LOCALE_HE_IL} from "../../../../contexts/ContextLib";

/**
 * Get automation table columns configuration
 * @param {object} params - Parameters object
 * @param {function} params.onAutomationSelectedForEdit - Callback for editing automation
 * @returns {array} Array of column configurations
 */
const getAutomationsColumns = ({onAutomationSelectedForEdit, i18n}) => {

    return [
        {
            field: 'automationName',
            headerName: i18n.translate("automationsTable.columns.automationName"),
            minWidth: 150,
            align: 'right',
            sortable: false,
            renderCell: (params) => <div style={{width: '100%'}}>{params.value}</div>,
        },
        {
            field: 'automationType',
            headerName: i18n.translate("automationsTable.columns.automationType"),
            minWidth: 120,
            align: 'right',
            sortable: false,
            renderCell: (params) => <div style={{width: '100%'}}>{params.value}</div>,
        },
        {
            field: 'description',
            headerName: i18n.translate("automationsTable.columns.description"),
            minWidth: 200,
            align: 'right',
            sortable: false,
            renderCell: (params) => <div style={{width: '100%'}} title={params.value}>{params.value}</div>,
        },
        {
            field: 'organizationName',
            headerName: i18n.translate("automationsTable.columns.organization"),
            minWidth: 180,
            align: 'right',
            sortable: false,
            renderCell: (params) => <ScrollableListCell value={params.value}/>,
        },
        {
            field: 'courseName',
            headerName: i18n.translate("automationsTable.columns.task"),
            minWidth: 180,
            align: 'right',
            sortable: false,
            renderCell: (params) => <ScrollableListCell value={params.value}/>,
        },
        {
            field: 'nextScheduled',
            headerName: i18n.translate("automationsTable.nextRun"),
            minWidth: 180,
            align: 'right',
            sortable: false,
            renderCell: (params) => <div style={{width: '100%'}} title={params.value}>{params.value}</div>,
        },
        {
            field: 'sendType',
            headerName: i18n.translate("automationsTable.columns.how"),
            minWidth: 150,
            align: 'right',
            sortable: false,
            renderCell: (params) => <div style={{width: '100%'}} title={params.value}>{params.value}</div>,
        },
        {
            field: 'status',
            headerName: i18n.translate("automationsTable.columns.status"),
            minWidth: 170,
            align: 'right',
            sortable: false,
            renderCell: (params) => <StatusCol status={params.value} i18n={i18n}/>,
        },
        // For feature implementation
        // {
        //     field: 'details',
        //     headerName: i18n.translate("automationsTable.columns.details"),
        //     minWidth: 100,
        //     align: 'right',
        //     sortable: false,
        //     renderCell: (params) => <AutomationDetailsCell automation={params.row}/>,
        // },
        {
            field: 'delete',
            headerName: i18n.translate("automationsTable.columns.delete"),
            minWidth: 100,
            align: 'right',
            sortable: false,
            renderCell: (params) => <DeleteAutomationCell automation={params.row}/>,
        },
        {
            field: 'edit',
            headerName: i18n.translate("automationsTable.columns.edit"),
            minWidth: 100,
            align: 'right',
            sortable: false,
            renderCell: (params) => <EditAutomationCell automation={params.row} onAutomationSelectedForEdit={onAutomationSelectedForEdit}/>,
        }
    ];
}

const tableFilters = {
    showFilters: {
        organizations: true,
        search: true,
    },
    filterValues: {},
    filterData: {}
}

/**
 * Status column component
 * @param {object} params - Parameters object
 * @param {string} params.status - Automation status
 * @returns {JSX.Element} Status column component
 */
const StatusCol = ({status, i18n}) => {
    const textStatus = i18n.translate(`automationsTable.status.${status}`)

    const getStatusIcon = () => {
        switch (status) {
            case AutomationStatus.SCHEDULED:
                return <ScheduleIcon style={{marginLeft: '6px'}} />
            case AutomationStatus.COMPLETED:
                return <CheckCircleIcon style={{marginLeft: '6px'}} />
            case AutomationStatus.PAUSED:
                return <PauseIcon style={{marginLeft: '6px'}} />
            default:
                return <SendIcon style={{marginLeft: '6px'}} />
        }
    }

    return <div className={`statusCol ${status}`} style={{width: '100%'}}>
        <div className="statusCol-token">
            <div className="statusInCol">{textStatus}</div>
            {getStatusIcon()}
        </div>
    </div>
}

/**
 * Convert automation data for table display
 * @param {array} automations - Array of automation objects
 * @returns {array} Converted automation data
 */
const convertData = (automations, i18n) => {
    return map(automations, (automation) => {
        const isRecurring = automation.schedulingMode === SchedulingModes.RECURRING_SCHEDULE

        // Handle comma-separated message types (e.g., "SMS,EMAIL,WHATSAPP")
        const messageTypesString = get(automation, 'type', '');
        const messageTypes = messageTypesString ? messageTypesString.split(',') : [];
        const translatedTypes = messageTypes.map(type =>
            i18n.translate(`automationsTable.sendType.${type.trim()}`)
        ).join(', ');

        return {
            ...automation,
            automationName: get(automation, 'name'),
            automationType: isRecurring
                ? i18n.translate('automationsTable.automationTypeBadge.recurring')
                : i18n.translate('automationsTable.automationTypeBadge.oneTime'),
            description: get(automation, 'description'),
            organizationName: get(automation, 'organizationName', []).join(', '),
            courseName: get(automation, 'courseName', []).join(', '),
            sendType: translatedTypes,
            nextScheduled: automation.nextRunDate
                ? new Date(automation.nextRunDate).toLocaleString(LOCALE_HE_IL)
                : i18n.translate(`automationsTable.status.${automation.status}`),
            schedulingMode: get(automation, 'schedulingMode', SchedulingModes.RELATIVE_TO_TASK_END),
            currentRuns: get(automation, 'currentRuns', 0),
            maxRuns: get(automation, 'maxRuns'),
            nextRunDate: get(automation, 'nextRunDate'),
            lastRunDate: get(automation, 'lastRunDate')
        }
    })
}

const paginationModel = {
    page: 0,
    pageSize: 20,
}

export const AutomationsTable = () => {
    const {i18n} = useContext(I18nReactContext);
    const {adminOrganizationOptions, isOrgsLoading} = useUserAdminOrgs({withAllOption: true})
    const [localTableFilters, setLocalTableFilters] = useState(null)

    const [editAutomationModalOpen, setEditAutomationModalOpen] = useState(false)
    const [editAutomation, setEditAutomation] = useState(null)

    const onAutomationSelectedForEdit = (automation) => {
        setEditAutomation(automation)
    }
    const closeEditAutomationModal = () => {
        setEditAutomation(null)
        setEditAutomationModalOpen(false)
    }

    useEffect(() => {
        if(!editAutomation){
            return
        }
        setEditAutomationModalOpen(true)
    }, [editAutomation]);

    useEffect(() => {
        if(isOrgsLoading){
            return
        }
        setLocalTableFilters({
            ...tableFilters,
            filterData: {
                ...tableFilters.filterData,
                organizations: adminOrganizationOptions
            },
            filterValues: {
                ...tableFilters.filterValues,
                organizationIds: [get(adminOrganizationOptions, '[0].value')]
            }
        })

    }, [isOrgsLoading, adminOrganizationOptions]);

    const getColumns = async ({paginationModel, filterModel}) => {
        return getAutomationsColumns({onAutomationSelectedForEdit, i18n})
    }

    /**
     * API function to get automation data for the table
     * @param {object} params - Parameters object
     * @param {object} params.paginationModel - Pagination parameters
     * @param {object} params.filterModel - Filter parameters
     * @param {object} params.sortModel - Sort parameters
     * @returns {Promise<object>} Promise resolving to automation data
     */
    const apiToGetData = async ({paginationModel, filterModel, sortModel}) => {
        return getCompanyAutomations({
            paginationModel,
            filterModel,
        })
    }

    if(!localTableFilters){
        return <LoadingSpinner/>
    }

    return <div className="automations-table">
        <DataGridWrapper
            filterModel={localTableFilters}
            disableColumnFilter={true}
            paginationModel={paginationModel}
            getColumns={getColumns}
            apiToGetData={apiToGetData}
            dataConverter={(data) => convertData(data, i18n)}
        />
        {editAutomationModalOpen && editAutomation ? <CreateAutomationModal key="automationEdit"
                                                      automationModalOpen={editAutomationModalOpen}
                                                      automation={editAutomation}
                                                      closeAutomationModal={closeEditAutomationModal}/> : ""}
    </div>
}